// ─── FILE SYSTEM ADAPTER ──────────────────────────────────────────────────────
// All GRC persistence via the File System Access API.
// Handles are stored in IndexedDB so the user is not re-prompted on every visit.

const DB_NAME = 'grc-fs';
const DB_VERSION = 1;
const STORE = 'handles';
const HANDLE_KEY = 'grc-root-handle';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      e.target.result.createObjectStore(STORE);
    };
    req.onsuccess = (e) => resolve(e.target.result);
    req.onerror = (e) => reject(e.target.error);
  });
}

/** Persist the root directory handle in IndexedDB. */
export async function initRootHandle(dirHandle) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).put(dirHandle, HANDLE_KEY);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

/** Retrieve the root directory handle from IndexedDB. Returns null if absent. */
export async function getRootHandle() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(HANDLE_KEY);
    req.onsuccess = (e) => resolve(e.target.result || null);
    req.onerror = (e) => reject(e.target.error);
  });
}

/** Remove the root handle entry from IndexedDB. */
export async function clearRootHandle() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const req = tx.objectStore(STORE).delete(HANDLE_KEY);
    req.onsuccess = () => resolve();
    req.onerror = (e) => reject(e.target.error);
  });
}

/**
 * Verify the stored root handle has (or can obtain) readwrite permission.
 *
 * - Retrieves the handle from IndexedDB; returns false if absent.
 * - Calls queryPermission({ mode: 'readwrite' }); returns true if 'granted'.
 * - If the state is 'prompt', calls requestPermission({ mode: 'readwrite' })
 *   and returns true only when the user grants it.
 * - Returns false for 'denied', missing handles, stale handles, or any error.
 */
export async function verifyRootHandle() {
  try {
    const handle = await getRootHandle();
    if (!handle) return false;
    const state = await handle.queryPermission({ mode: 'readwrite' });
    if (state === 'granted') return true;
    if (state === 'prompt') {
      const result = await handle.requestPermission({ mode: 'readwrite' });
      return result === 'granted';
    }
    return false;
  } catch {
    return false;
  }
}

// ─── INTERNAL HELPERS ─────────────────────────────────────────────────────────

/**
 * Navigate from the org root to the directory that contains filePath,
 * creating intermediate directories if `create` is true.
 * Returns { dir: FileSystemDirectoryHandle, fileName: string }.
 */
async function getDirAndFileName(rootHandle, orgId, filePath, create) {
  let dir = await rootHandle.getDirectoryHandle(orgId, { create });
  const parts = filePath.split('/');
  for (let i = 0; i < parts.length - 1; i++) {
    dir = await dir.getDirectoryHandle(parts[i], { create });
  }
  return { dir, fileName: parts[parts.length - 1] };
}

// ─── PUBLIC API ───────────────────────────────────────────────────────────────

/**
 * Read and parse a JSON file at [root]/[orgId]/[filePath].
 * Throws if the root handle is not set or the file does not exist.
 */
export async function readOrgFile(orgId, filePath) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) throw new Error('No root handle configured');
  const { dir, fileName } = await getDirAndFileName(rootHandle, orgId, filePath, false);
  const fileHandle = await dir.getFileHandle(fileName);
  const file = await fileHandle.getFile();
  const text = await file.text();
  return JSON.parse(text);
}

/**
 * Serialise `data` as JSON and write it to [root]/[orgId]/[filePath].
 * Intermediate directories are created as needed.
 */
export async function writeOrgFile(orgId, filePath, data) {
  if (!orgId || filePath.includes('..') || filePath.startsWith('/')) {
    throw new Error('Invalid write path: orgId or filePath failed validation');
  }
  const rootHandle = await getRootHandle();
  if (!rootHandle) throw new Error('No root handle configured');
  const { dir, fileName } = await getDirAndFileName(rootHandle, orgId, filePath, true);
  const fileHandle = await dir.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(JSON.stringify(data, null, 2));
  await writable.close();
}

/**
 * Return the names of all subdirectories in the root folder.
 * Each subdirectory corresponds to one organisation.
 */
export async function listOrgs() {
  const rootHandle = await getRootHandle();
  if (!rootHandle) return [];
  const names = [];
  for await (const [name, handle] of rootHandle.entries()) {
    if (handle.kind === 'directory') names.push(name);
  }
  return names;
}

/**
 * Return true if [root]/[orgId]/ exists, false otherwise.
 */
export async function orgExists(orgId) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) return false;
  try {
    await rootHandle.getDirectoryHandle(orgId, { create: false });
    return true;
  } catch {
    return false;
  }
}

/**
 * Return true if the file at [root]/[orgId]/[filePath] exists.
 */
export async function orgFileExists(orgId, filePath) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) return false;
  try {
    const { dir, fileName } = await getDirAndFileName(rootHandle, orgId, filePath, false);
    await dir.getFileHandle(fileName);
    return true;
  } catch {
    return false;
  }
}

/**
 * Return all parsed JSON objects from every .json file inside
 * [root]/[orgId]/[folderPath]/. Returns [] if folder absent.
 */
export async function listOrgCollection(orgId, folderPath) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) return [];
  try {
    let dir = await rootHandle.getDirectoryHandle(orgId, { create: false });
    const parts = folderPath.split('/').filter(Boolean);
    for (const part of parts) {
      dir = await dir.getDirectoryHandle(part, { create: false });
    }
    const items = [];
    for await (const [, handle] of dir.entries()) {
      if (handle.kind === 'file' && handle.name.endsWith('.json')) {
        const file = await handle.getFile();
        const text = await file.text();
        try { items.push(JSON.parse(text)); } catch { /* skip malformed */ }
      }
    }
    return items;
  } catch (e) {
    if (e && e.name === 'NotFoundError') return [];
    throw e;
  }
}

/**
 * Delete the file at [root]/[orgId]/[filePath].
 * Throws if the root handle is not set or the file does not exist.
 */
export async function deleteOrgFile(orgId, filePath) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) throw new Error('No root handle configured');
  const { dir, fileName } = await getDirAndFileName(rootHandle, orgId, filePath, false);
  await dir.removeEntry(fileName);
}

/**
 * Return true if [root]/[orgId]/[folderPath]/ exists and contains at least
 * one .json file.
 */
export async function orgCollectionHasEntries(orgId, folderPath) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) return false;
  try {
    let dir = await rootHandle.getDirectoryHandle(orgId, { create: false });
    const parts = folderPath.split('/').filter(Boolean);
    for (const part of parts) {
      dir = await dir.getDirectoryHandle(part, { create: false });
    }
    for await (const [, handle] of dir.entries()) {
      if (handle.kind === 'file' && handle.name.endsWith('.json')) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// ─── BINARY I/O ──────────────────────────────────────────────────────────────

/**
 * Write a Blob/File to [root]/[orgId]/[filePath] without JSON serialization.
 * Same path validation and intermediate-directory behaviour as writeOrgFile.
 */
export async function writeOrgBinary(orgId, filePath, blob) {
  if (!orgId || filePath.includes('..') || filePath.startsWith('/')) {
    throw new Error('Invalid write path: orgId or filePath failed validation');
  }
  const rootHandle = await getRootHandle();
  if (!rootHandle) throw new Error('No root handle configured');
  const { dir, fileName } = await getDirAndFileName(rootHandle, orgId, filePath, true);
  const fileHandle = await dir.getFileHandle(fileName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/**
 * Read the binary file at [root]/[orgId]/[filePath] and return the underlying
 * File object. Returns null if the file does not exist (does not throw on
 * absence).
 */
export async function readOrgBinary(orgId, filePath) {
  const rootHandle = await getRootHandle();
  if (!rootHandle) throw new Error('No root handle configured');
  try {
    const { dir, fileName } = await getDirAndFileName(rootHandle, orgId, filePath, false);
    const fileHandle = await dir.getFileHandle(fileName);
    return await fileHandle.getFile();
  } catch (e) {
    if (e && e.name === 'NotFoundError') return null;
    throw e;
  }
}

/**
 * Delete the binary at [root]/[orgId]/[filePath]. Provided as a separate
 * exported name (delegates to deleteOrgFile) only for caller clarity.
 */
export async function deleteOrgBinary(orgId, filePath) {
  return deleteOrgFile(orgId, filePath);
}

/**
 * Return an Object URL for the binary at [root]/[orgId]/[filePath] via
 * URL.createObjectURL. Returns null if the file does not exist. Caller is
 * responsible for revoking the URL via URL.revokeObjectURL.
 */
export async function orgBinaryUrl(orgId, filePath) {
  const file = await readOrgBinary(orgId, filePath);
  if (!file) return null;
  return URL.createObjectURL(file);
}

/**
 * Atomic bidirectional write per §11 rule 39.
 *
 * Writes the primary file first, then the secondary file. If the secondary
 * write fails, the primary side is rolled back before the error is re-thrown
 * so that partial bidirectional state is never persisted.
 *
 * Spec shape for each side:
 *   { path: string, data: any, previousData?: any }
 *
 * - `path`       — relative path under [root]/[orgId]/ (same conventions as writeOrgFile)
 * - `data`       — object to serialize as the new contents
 * - `previousData` (optional) — contents to restore if rollback is needed.
 *                  When omitted, the rollback path is `deleteOrgFile(primary)`,
 *                  which is the correct behaviour for a first-time write
 *                  (no prior file to restore). When provided, the prior
 *                  contents are written back so an existing record is
 *                  preserved exactly as it was.
 *
 * On success, returns nothing. On failure, throws the original error after
 * attempting rollback (rollback is best-effort and never surfaces).
 */
export async function writeBidirectional(orgId, primarySpec, secondarySpec) {
  await writeOrgFile(orgId, primarySpec.path, primarySpec.data);
  try {
    await writeOrgFile(orgId, secondarySpec.path, secondarySpec.data);
  } catch (secondaryErr) {
    try {
      if (primarySpec.previousData !== undefined && primarySpec.previousData !== null) {
        await writeOrgFile(orgId, primarySpec.path, primarySpec.previousData);
      } else {
        await deleteOrgFile(orgId, primarySpec.path);
      }
    } catch {
      /* rollback best-effort — swallow so the caller sees the original error */
    }
    throw secondaryErr;
  }
}
